// Time presentation for the call surfaces: slot times, day chips and the
// booked-call line, all in the viewer's own timezone (the mockup hardcodes
// Mountain; a Florida lead shouldn't have to convert). Every formatter
// falls back rather than throwing — a locale quirk must never take down
// the band.

/** The company clock — the fallback whenever the browser won't say. */
export const COMPANY_TIME_ZONE = "America/Denver";

export function browserTimeZone(): string {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || COMPANY_TIME_ZONE
    );
  } catch {
    return COMPANY_TIME_ZONE;
  }
}

function timeZonePart(
  timeZone: string,
  timeZoneName: "short" | "shortGeneric" | "longGeneric",
  at: Date = new Date(),
): string | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName,
    }).formatToParts(at);
    return parts.find((part) => part.type === "timeZoneName")?.value ?? null;
  } catch {
    return null;
  }
}

/** "Mountain Time" — the time-grid heading's qualifier. */
export function timeZoneLongLabel(timeZone: string): string {
  return (
    timeZonePart(timeZone, "longGeneric") ??
    timeZonePart(timeZone, "short") ??
    ""
  );
}

/** "MT" (or "MDT" where the runtime lacks generic names) — rides beside a
 * specific clock time. */
export function timeZoneShortLabel(timeZone: string): string {
  return (
    timeZonePart(timeZone, "shortGeneric") ??
    timeZonePart(timeZone, "short") ??
    ""
  );
}

/** The zone's abbreviation in force at the call's own instant ("MDT" in
 * October, "MST" in November) — never at the moment of reading. */
export function callZoneAbbreviation(
  startIso: string,
  timeZone: string,
): string {
  const at = new Date(startIso);
  if (Number.isNaN(at.getTime())) return "";
  return timeZonePart(timeZone, "short", at) ?? "";
}

/** "Fri, Oct 3" for the call's UTC start, in the given timezone. */
export function callDayLabel(startIso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(new Date(startIso));
  } catch {
    return startIso;
  }
}

/** "2:30 PM" for a slot's UTC start. */
export function slotTimeLabel(startIso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(startIso));
  } catch {
    return startIso;
  }
}

/** Within each half-hour, the minute a slot is offered at, best first: the
 * mark itself, then the ten-minute steps that follow it. */
const HALF_HOUR_PREFERENCES = [
  [0, 10, 20],
  [30, 40, 50],
] as const;

function hourAndMinute(
  startIso: string,
  timeZone: string,
): { hour: string; minute: number } | null {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(startIso));
    const hour = parts.find((part) => part.type === "hour")?.value;
    const minute = parts.find((part) => part.type === "minute")?.value;
    if (hour === undefined || minute === undefined) return null;
    return { hour, minute: Number(minute) };
  } catch {
    return null;
  }
}

/**
 * Thins a day's bookable slots to two an hour — the top of the hour and
 * the half past — so the grid reads as a handful of choices rather than a
 * wall of them. When the preferred minute is not bookable the next
 * ten-minute step in that half stands in: 8:00, or 8:10 if 8:00 is gone,
 * or 8:20 if both are. So no half-hour with any availability disappears,
 * and none contributes more than one time.
 *
 * Times the formatter cannot read are kept: a locale quirk must never
 * hide availability.
 */
export function preferredSlots(
  times: readonly string[],
  timeZone: string,
): string[] {
  const byHour = new Map<string, Map<number, string>>();
  const kept = new Set<string>();

  for (const time of times) {
    const at = hourAndMinute(time, timeZone);
    if (at === null) {
      kept.add(time);
      continue;
    }
    const key = `${calendarDate(new Date(time), timeZone)}T${at.hour}`;
    const hour = byHour.get(key) ?? new Map<number, string>();
    if (!hour.has(at.minute)) hour.set(at.minute, time);
    byHour.set(key, hour);
  }

  for (const hour of byHour.values()) {
    for (const preferences of HALF_HOUR_PREFERENCES) {
      const minute = preferences.find((candidate) => hour.has(candidate));
      if (minute !== undefined) kept.add(hour.get(minute) as string);
    }
  }

  // The input is already ascending; filtering it keeps that.
  return times.filter((time) => kept.has(time));
}

/**
 * The day whose times the grid shows: the picked one, or the only one
 * offered. A lone day is not a choice, so its times show without a tap;
 * two or more show nothing until one is picked. A pick that is no longer
 * offered counts as no pick.
 */
export function dayOnShow<Day extends { date: string }>(
  days: readonly Day[],
  pickedDate: string | null,
): Day | null {
  const picked = days.find((day) => day.date === pickedDate);
  if (picked) return picked;
  return days.length === 1 ? days[0] : null;
}

/** The calendar date (`2026-09-01`) an instant falls on in a timezone. */
function calendarDate(at: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(at);
  } catch {
    return at.toISOString().slice(0, 10);
  }
}

/** Noon UTC pins a bare `2026-09-01` to its own calendar day in any
 * timezone a viewer plausibly sits in. */
function dateAnchor(date: string): Date {
  return new Date(`${date}T12:00:00Z`);
}

function relativeDayName(date: string, timeZone: string): string | null {
  const now = new Date();
  if (date === calendarDate(now, timeZone)) return "Today";
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  if (date === calendarDate(tomorrow, timeZone)) return "Tomorrow";
  return null;
}

/** A day chip's face: `{ label: "Today", sub: "Tue, Sep 1" }` or
 * `{ label: "Wednesday", sub: "Sep 2" }`. */
export function dayChipLabels(
  date: string,
  timeZone: string,
): { label: string; sub: string } {
  const anchor = dateAnchor(date);
  const relative = relativeDayName(date, timeZone);
  try {
    if (relative) {
      return {
        label: relative,
        sub: new Intl.DateTimeFormat("en-US", {
          timeZone: "UTC",
          weekday: "short",
          month: "short",
          day: "numeric",
        }).format(anchor),
      };
    }
    return {
      label: new Intl.DateTimeFormat("en-US", {
        timeZone: "UTC",
        weekday: "long",
      }).format(anchor),
      sub: new Intl.DateTimeFormat("en-US", {
        timeZone: "UTC",
        month: "short",
        day: "numeric",
      }).format(anchor),
    };
  } catch {
    return { label: date, sub: "" };
  }
}

/** The booked line's day, from the call's UTC start: "Today", "Tomorrow"
 * or "Wed, Sep 2". */
export function bookedDayLabel(startIso: string, timeZone: string): string {
  try {
    const date = calendarDate(new Date(startIso), timeZone);
    return (
      relativeDayName(date, timeZone) ??
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "short",
        month: "short",
        day: "numeric",
      }).format(new Date(startIso))
    );
  } catch {
    return startIso;
  }
}
